import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiExclamationTriangle, HiChartBar } from "react-icons/hi2";
import { CandidateService } from "@/services/CandidateService";
import { ScoreBadge, evaluationStatusFromApi, EVALUATION_STATUS } from "@/scoringUi";
import { ModelService } from "@/services/ModelService";
import ScoreBreakdown from "./ScoreBreakdown";
import AiRecommendationHeader from "./AiRecommendationHeader";
import useScoreLabels from "./useScoreLabels";
import { CollapsibleSection, Grid3 } from "@/components/jobOffer/ui";

/**
 * "Ocena" tab (spec 2 §9-11): the client's sole view of a candidate's score
 * -- reads GET /api/candidate/{id}/evaluation as-is, never recomputes
 * anything (AC-FE-14). No scenario-vs-recruitment split is shown here (spec
 * §12); that detail is only in the raw `source` field per breakdown row,
 * exposed here as a small technical label, not a primary UI element.
 */
const CandidateEvaluationTab = ({ candidateId, recruitmentId }) => {
    const { t } = useTranslation("models");
    const { labels: scoreLabels } = useScoreLabels();
    const [scoringModel, setScoringModel] = useState(null);
    const [evaluation, setEvaluation] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isRecalculating, setIsRecalculating] = useState(false);

    const fetchEvaluation = () => {
        setIsLoading(true);
        setHasError(false);
        CandidateService.getEvaluation(candidateId)
            .then(setEvaluation)
            .catch((err) => {
                if (err?.response?.status === 404) {
                    setEvaluation(null);
                } else {
                    setHasError(true);
                }
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (recruitmentId) ModelService.getRecruitmentScoringModel(recruitmentId).then(setScoringModel).catch(() => setScoringModel(null));
    }, [recruitmentId]);

    useEffect(() => {
        fetchEvaluation();
    }, [candidateId]);

    const handleRecalculate = async () => {
        setIsRecalculating(true);
        try {
            const updated = await CandidateService.recalculateEvaluation(candidateId);
            setEvaluation(updated);
        } catch {
            setHasError(true);
        } finally {
            setIsRecalculating(false);
        }
    };

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="purple.400" size="lg" />
            </VStack>
        );
    }

    if (hasError) {
        return (
            <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius={12} p={5}>
                <Text color="red.600" fontSize="sm">{t("candidateDetailPage.evaluationLoadError")}</Text>
            </Box>
        );
    }

    const status = evaluation ? evaluationStatusFromApi(evaluation.status) : null;

    // Distinct from "0 punktów": no CandidateEvaluation row exists yet, or the
    // conversation hasn't produced enough data (IN_PROGRESS). Must never look
    // like a scored 0 -- spec §29-equivalent requirement (§13 UI states).
    if (!evaluation || status === EVALUATION_STATUS.IN_PROGRESS) {
        return (
            <VStack w="full" bg="white" border="1px dashed" borderColor="gray.200" borderRadius={12} p={8} gap={2}>
                <Text color="gray.500" fontSize="sm" fontStyle="italic">
                    {t("candidateDetailPage.evaluationNotAvailable")}
                </Text>
                <Button size="sm" variant="outlined" onClick={handleRecalculate} loading={isRecalculating}>
                    {t("candidateDetailPage.evaluationRecalculate")}
                </Button>
            </VStack>
        );
    }

    const isDisqualified = status === EVALUATION_STATUS.DISQUALIFIED;
    const isNeedsReview = status === EVALUATION_STATUS.NEEDS_REVIEW;

    return (
        <VStack gap={4} align="stretch">
            {/* Header: score, status, completeness */}
            <CollapsibleSection
                title={t("candidateDetailPage.tabEvaluation")}
                icon={HiChartBar}
                defaultOpen
                action={
                    <Button size="sm" variant="outlined" onClick={(e) => { e.stopPropagation(); handleRecalculate(); }} loading={isRecalculating}>
                        {t("candidateDetailPage.evaluationRecalculate")}
                    </Button>
                }
            >
                <Grid3>
                    <VStack align="flex-start" gap={1}>
                        <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                            {t("candidateDetailPage.evaluationScore")}
                        </Text>
                        <Text color="gray.900" fontWeight={700} fontSize="2xl">
                            {evaluation.scoreRaw} / {evaluation.scoreMax}
                            <Text as="span" color="gray.500" fontSize="sm" fontWeight={400}> ({Math.round(evaluation.scorePercent)}%)</Text>
                        </Text>
                    </VStack>
                    <VStack align="flex-start" gap={1}>
                        <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                            {t("candidateDetailPage.evaluationStatus")}
                        </Text>
                        <ScoreBadge evaluation={{ scorePercent: evaluation.scorePercent, status }} labels={scoreLabels} size="md" />
                    </VStack>
                    <VStack align="flex-start" gap={1}>
                        <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                            {t("candidateDetailPage.evaluationCompleteness")}
                        </Text>
                        <Text color="gray.900" fontWeight={600} fontSize="lg">{Math.round(evaluation.completeness * 100)}%</Text>
                    </VStack>
                </Grid3>
            </CollapsibleSection>

            {/* Disqualification banner -- visually dominant, never the same as a low score (spec §11/§29). */}
            {isDisqualified && (
                <HStack bg="red.50" border="1px solid" borderColor="red.200" borderRadius={12} p={4} gap={3} align="flex-start">
                    <Box color="red.600" fontSize="xl" flexShrink={0}><HiExclamationTriangle /></Box>
                    <VStack align="flex-start" gap={1}>
                        <Text color="red.700" fontWeight={700} fontSize="sm">{t("candidateDetailPage.evaluationDisqualified")}</Text>
                        {evaluation.disqualificationReasons?.length > 0 ? (
                            evaluation.disqualificationReasons.map((reason, i) => (
                                <Text key={i} color="red.700" fontSize="sm">• {reason}</Text>
                            ))
                        ) : (
                            <Text color="red.700" fontSize="sm">{t("candidateDetailPage.evaluationNoReasonGiven")}</Text>
                        )}
                    </VStack>
                </HStack>
            )}

            {isNeedsReview && !isDisqualified && (
                <HStack bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius={12} p={4} gap={3}>
                    <Box color="gray.500" fontSize="lg" flexShrink={0}><HiExclamationTriangle /></Box>
                    <Text color="gray.500" fontSize="sm">{t("candidateDetailPage.evaluationNeedsReview")}</Text>
                </HStack>
            )}

            <AiRecommendationHeader />

            <ScoreBreakdown rows={evaluation.criterionResults ?? []} model={scoringModel} candidateId={candidateId} onChanged={setEvaluation} />
        </VStack>
    );
};

export default CandidateEvaluationTab;
